// custom-fetch.ts

import { getAuthToken } from "@/core/auth/auth";
import { getTrackingHeaders } from "@/core/tracking";

export interface FetchError<T = unknown> extends Error {
  status: number;
  data: T;
}

const UUID_PATH_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_PATH_SEGMENT = /^\d+$/;

// Returns a low-cardinality template of the request path (query string dropped, UUIDs and
// all-numeric segments replaced with placeholders) so the resulting Error.message is stable
// per endpoint and Datadog Error Tracking can fingerprint failures distinctly per status code
// and route, instead of collapsing every non-2xx response into a single "API Error" bucket.
function buildErrorEndpointFingerprint(url: string): string {
  const pathOnly = url.split("?")[0];
  return pathOnly
    .split("/")
    .map((segment) => {
      if (UUID_PATH_SEGMENT.test(segment)) return ":id";
      if (NUMERIC_PATH_SEGMENT.test(segment)) return ":n";
      return segment;
    })
    .join("/");
}

function buildQueryString(params: Record<string, any> | undefined): string {
  if (!params || Object.keys(params).length === 0) return "";

  const filtered = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = String(value);
    }
    return acc;
  }, {});

  return `?${new URLSearchParams(filtered)}`;
}

/**
 * Build request headers with a well-defined priority:
 *   1. Defaults (Content-Type for non-FormData bodies)
 *   2. Auth token (automatic Bearer header)
 *   3. Tracking enrichers (centralized via tracking module)
 *   4. Caller-provided headers (highest priority — can override everything above)
 */
function buildHeaders(
  callerHeaders: HeadersInit | undefined,
  isFormData: boolean,
  authToken: string | null,
): Headers {
  const headers = new Headers();

  if (!isFormData) {
    headers.set("Content-Type", "application/json");
  }

  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
  }

  const trackingHeaders = getTrackingHeaders();
  trackingHeaders.forEach((value, key) => {
    headers.set(key, value);
  });

  if (callerHeaders) {
    const incoming = new Headers(callerHeaders);
    incoming.forEach((value, key) => {
      if (key.toLowerCase() === "authorization" && !value.trim()) {
        return;
      }
      headers.set(key, value);
    });
  }

  return headers;
}

/**
 * Custom fetch wrapper used as the orval mutator for all generated API clients.
 *
 * Key behaviors:
 * - **Body pass-through**: The body is forwarded to `fetch` as-is. Orval-generated
 *   callers already call `JSON.stringify`, so this function must NOT serialize again.
 * - **Error responses**: Any non-2xx status throws a {@link FetchError} containing the
 *   HTTP `status` and the parsed response `data` (JSON or text).
 * - **Network errors**: If `fetch` itself rejects (e.g. DNS failure, CORS), the raw
 *   error propagates without being wrapped in `FetchError`.
 */
export const retrieveData = async <TData, TError = unknown>(
  url: string,
  options: Pick<RequestInit, "method" | "body" | "signal" | "headers"> & {
    params?: Record<string, any>;
  },
): Promise<TData> => {
  const { method, params, body, signal, headers } = options;

  const baseURL = process.env.NEXT_PUBLIC_API_URL ?? "";
  const queryString = buildQueryString(params);
  const requestHeaders = buildHeaders(headers, body instanceof FormData, await getAuthToken());

  const response = await fetch(`${baseURL}${url}${queryString}`, {
    method,
    headers: requestHeaders,
    body: body ?? undefined,
    signal,
  });

  const contentType = response.headers.get("content-type");
  const rawResponse = await response.text();

  // Preserve historical contract: empty successful responses resolve to ''.
  let data: unknown = "";

  if (rawResponse.length > 0) {
    if (contentType?.includes("application/json")) {
      try {
        data = JSON.parse(rawResponse);
      } catch {
        // Some endpoints incorrectly return an empty/non-JSON body with JSON content-type.
        // Fall back to raw text so successful 2xx calls don't fail client-side parsing.
        data = rawResponse;
      }
    } else {
      data = rawResponse;
    }
  }

  if (!response.ok) {
    const error: FetchError<TError> = Object.assign(
      new Error(`API Error ${response.status} ${method} ${buildErrorEndpointFingerprint(url)}`),
      {
        status: response.status,
        data: data as TError,
      },
    );
    throw error;
  }

  return data as TData;
};

export type ErrorType<T> = FetchError<T>;
export type BodyType<T> = T;

export default retrieveData;
